/**
 * Axis labels for maneuver channels whose display name differs from the channel key.
 *
 * Two reasons a channel lands here: TAKEOFF renames "exit" to "pop", and the server's abs()
 * aliases carry an "_n" suffix that is plumbing, not something to print on an axis.
 *
 * Shared by ManeuverScatter (standard view) and ManeuverBoxPlot (grouped view) so a channel does
 * not read one way in one view and another way in the other.
 */
const CHANNEL_DISPLAY_LABELS: Record<string, string> = {
  exit_time: "POP_TIME",
  bsp_exit: "BSP_POP",
  twa_exit: "TWA_POP",
  bsp_entry: "BSP_ENTRY",
  bsp_max: "MAX_BSP",
  bsp_min: "MIN_BSP",
  twa_build_n: "TWA_BUILD",
  twa_accmax_n: "TWA_ACCMAX",
  abs_twa_entry: "TWA_ENTRY",
  abs_twa_final: "TWA_FINAL",
  abs_twa_start: "TWA_START",
};

/**
 * Labels that apply only to TAKEOFF, checked before the shared map above.
 *
 * pop_time means two different things: for tacks/gybes it is the wing loading up on the new tack,
 * and it keeps the POP_TIME fallback. For a takeoff it is the moment the boat reached 32 KPH, and
 * the takeoff plots ALSO carry exit_time -- which the shared map already prints as POP_TIME, since
 * a takeoff has no turn to exit. Without this override both panels would be labelled POP_TIME.
 */
const TAKEOFF_CHANNEL_DISPLAY_LABELS: Record<string, string> = {
  pop_time: "BUILD_TIME",
};

/**
 * Display label for a maneuver channel; falls back to the uppercased channel key.
 *
 * `eventType` is optional so existing call sites keep today's behaviour; pass it wherever the
 * event type is known, since a few channel names are reused across types with different meanings.
 */
export function getManeuverChannelDisplayLabel(channel: string, eventType?: string | null): string {
  const ch = String(channel || "").toLowerCase();
  if (String(eventType ?? "").trim().toUpperCase() === "TAKEOFF" && TAKEOFF_CHANNEL_DISPLAY_LABELS[ch]) {
    return TAKEOFF_CHANNEL_DISPLAY_LABELS[ch];
  }
  return CHANNEL_DISPLAY_LABELS[ch] ?? String(channel || "").toUpperCase();
}
